/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { useTrackModuleView, useTrackSearch } from "@/lib/activityTracker";
import LangSwitch from "@/components/LangSwitch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import PhotoCapture from "@/components/animals/PhotoCapture";
import AnimalDetailDialog from "@/components/animals/AnimalDetailDialog";
import {
  Plus,
  MoreVertical,
  ArrowLeft,
  Search,
  Beef,
  LogOut,
  HeartPulse,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPECIES = ["Bovins", "Ovins", "Caprins", "Lapins", "Autre"];
const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800",
  sold: "bg-blue-100 text-blue-800",
  dead: "bg-zinc-200 text-zinc-700",
};

const empty = {
  tag: "",
  name: "",
  species: "Bovins",
  breed: "",
  sex: "F",
  birth_date: "",
  weight_kg: "",
  notes: "",
  photo_url: null,
  mother_id: "",
  father_id: "",
  origin: "born_farm",
  purchase_price_tnd: "",
  purchase_date: "",
  supplier: "",
};

export default function Animals() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [data, setData] = useState({ animals: [], stats: null });
  const [loadingData, setLoadingData] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSpecies, setFilterSpecies] = useState("all");
  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);

  useTrackModuleView("animals");
  useTrackSearch("animals", search);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const { data } = await axios.get(`${API}/animals`, {
        withCredentials: true,
      });
      setData(data);
    } catch (e) {
      toast.error(t.toasts.error_title, {
        description: formatApiError(e?.response?.data?.detail),
      });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "client" && user.status === "active") fetchData();
  }, [user]);

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">
        {t.app.common.loading}
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  if (user.status !== "active") return <Navigate to="/dashboard/client" replace />;

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/animals/${deleteTarget.id}`, {
        withCredentials: true,
      });
      toast.success(t.app.animals.deleted_toast);
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(t.toasts.error_title, {
        description: formatApiError(e?.response?.data?.detail),
      });
    }
  };

  const filtered = data.animals.filter((a) => {
    if (filterSpecies !== "all" && a.species !== filterSpecies) return false;
    const q = search.toLowerCase();
    return (
      !q ||
      a.tag?.toLowerCase().includes(q) ||
      a.name?.toLowerCase().includes(q) ||
      a.breed?.toLowerCase().includes(q)
    );
  });

  const stats = data.stats || { total: 0, by_species: {}, by_status: {} };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1C3A27] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard/client" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-white/70" />
            <span className="font-display text-lg font-bold">Mazra3ti</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-white/80"
            >
              <LogOut size={16} className="me-2" /> {t.app.common.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
              <Beef size={14} /> {t.app.animals.overline}
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
              {t.app.animals.title}
            </h1>
            <p className="mt-1 text-[#5C6B5C]">
              {t.app.animals.subtitle}
            </p>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-create-animal"
                className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold"
              >
                <Plus size={18} className="me-2" /> {t.app.animals.new_animal}
              </Button>
            </DialogTrigger>
            <AnimalDialogContent
              title={t.app.animals.add_title}
              animals={data.animals || []}
              onClose={() => setOpenCreate(false)}
              onSaved={() => {
                setOpenCreate(false);
                fetchData();
              }}
            />
          </Dialog>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <StatCard label={t.app.animals.stat_total} value={stats.total} />
          <StatCard label={t.app.animals.stat_active} value={stats.by_status.active || 0} accent="#1C7A3C" />
          <StatCard label={t.app.animals.stat_sold} value={stats.by_status.sold || 0} accent="#1E6BBA" />
          <StatCard label={t.app.animals.stat_dead} value={stats.by_status.dead || 0} accent="#8A8A8A" />
        </div>

        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]"
            />
            <Input
              data-testid="animals-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.app.animals.search_placeholder}
              className="ps-10 h-11 bg-white border-[#E2E4DF] rounded-xl"
            />
          </div>
          <Select value={filterSpecies} onValueChange={setFilterSpecies}>
            <SelectTrigger className="w-44 h-11 bg-white border-[#E2E4DF] rounded-xl">
              <SelectValue placeholder={t.app.animals.species_label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.app.animals.all_species}</SelectItem>
              {SPECIES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t.app.animals.species[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
          {loadingData ? (
            <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Beef size={40} className="mx-auto text-[#5C6B5C] opacity-40" />
              <p className="mt-3 text-[#5C6B5C]">
                {data.animals.length === 0
                  ? t.app.animals.empty_first
                  : t.app.animals.empty_search}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-start px-3 py-3 font-semibold w-12">Photo</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_tag}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_name}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_species_breed}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_sex}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_birth}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_weight}</th>
                    <th className="text-start px-5 py-3 font-semibold">{t.app.animals.th_status}</th>
                    <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr
                      key={a.id}
                      data-testid={`animal-row-${a.id}`}
                      className="border-t border-[#E2E4DF]"
                    >
                      <td className="px-3 py-2 w-12">
                        {a.photo_url ? (
                          <img src={a.photo_url} alt={a.tag} className="w-10 h-10 rounded-lg object-cover border border-[#E2E4DF]" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-[#F0F0EB] grid place-items-center text-[#5C6B5C]/40">
                            <Beef size={16} />
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 font-mono font-semibold text-[#1C3A27]">
                        {a.tag}
                      </td>
                      <td className="px-5 py-4 text-[#1C3A27]">
                        {a.name || "—"}
                      </td>
                      <td className="px-5 py-4 text-[#5C6B5C]">
                        {t.app.animals.species[a.species] || a.species}
                        {a.breed && (
                          <span className="text-xs"> · {a.breed}</span>
                        )}
                      </td>
                      <td className="px-5 py-4">{a.sex === "F" ? `♀ ${t.app.animals.female}` : `♂ ${t.app.animals.male}`}</td>
                      <td className="px-5 py-4 text-[#5C6B5C]">
                        {a.birth_date || "—"}
                      </td>
                      <td className="px-5 py-4 text-[#5C6B5C]">
                        {a.weight_kg ? `${a.weight_kg} kg` : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[a.status]}`}
                        >
                          {t.app.status[a.status]}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-end">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setDetailTarget(a)}
                            data-testid={`soins-btn-${a.id}`}
                            title="Soins & Suivi"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E05A3D]/10 hover:bg-[#E05A3D] text-[#C84B31] hover:text-white font-bold text-xs uppercase tracking-wider border border-[#E05A3D]/30 transition-colors"
                          >
                            <HeartPulse size={14} />
                            <span className="hidden sm:inline">Soins</span>
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`animal-actions-${a.id}`}
                                className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#5C6B5C]"
                              >
                                <MoreVertical size={18} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setEditTarget(a)}
                                data-testid={`edit-${a.id}`}
                              >
                                {t.app.common.edit}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(a)}
                                data-testid={`delete-${a.id}`}
                                className="text-red-700"
                              >
                                {t.app.common.delete}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Dialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
      >
        {editTarget && (
          <AnimalDialogContent
            title={t.app.animals.edit_title}
            initial={editTarget}
            animals={data.animals || []}
            onClose={() => setEditTarget(null)}
            onSaved={() => {
              setEditTarget(null);
              fetchData();
            }}
          />
        )}
      </Dialog>

      <AnimalDetailDialog
        animal={detailTarget}
        animals={data.animals || []}
        open={!!detailTarget}
        onClose={() => setDetailTarget(null)}
        onChanged={fetchData}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.app.animals.delete_title}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  {deleteTarget.tag}
                  {deleteTarget.name ? ` — ${deleteTarget.name}` : ""}.{" "}
                  {t.app.common.irreversible}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-animal"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t.app.common.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, accent = "#1C3A27" }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-5">
      <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold">
        {label}
      </div>
      <div
        className="font-display text-3xl font-black mt-1"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function AnimalDialogContent({ title, initial, animals = [], onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? {
          tag: initial.tag || "",
          name: initial.name || "",
          species: initial.species || "Bovins",
          breed: initial.breed || "",
          sex: initial.sex || "F",
          birth_date: initial.birth_date || "",
          weight_kg: initial.weight_kg ?? "",
          status: initial.status || "active",
          notes: initial.notes || "",
          photo_url: initial.photo_url || null,
          mother_id: initial.mother_id || "",
          father_id: initial.father_id || "",
          origin: initial.origin || "born_farm",
          purchase_price_tnd: initial.purchase_price_tnd ?? "",
          purchase_date: initial.purchase_date || "",
          supplier: initial.supplier || "",
        }
      : empty
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Filter animals for parent selection (exclude self, match sex)
  const mothers = animals.filter((a) => a.sex === "F" && a.id !== initial?.id);
  const fathers = animals.filter((a) => a.sex === "M" && a.id !== initial?.id);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const payload = {
      tag: form.tag.trim(),
      name: form.name.trim() || undefined,
      species: form.species,
      breed: form.breed.trim() || undefined,
      sex: form.sex,
      birth_date: form.birth_date || undefined,
      weight_kg:
        form.weight_kg === "" ? undefined : parseFloat(form.weight_kg),
      notes: form.notes.trim() || undefined,
      photo_url: form.photo_url || undefined,
      mother_id: form.mother_id || undefined,
      father_id: form.father_id || undefined,
      origin: form.origin || "born_farm",
      purchase_price_tnd:
        form.origin === "purchased" && form.purchase_price_tnd !== ""
          ? parseFloat(form.purchase_price_tnd)
          : undefined,
      purchase_date: form.origin === "purchased" ? (form.purchase_date || undefined) : undefined,
      supplier: form.origin === "purchased" ? (form.supplier.trim() || undefined) : undefined,
    };
    if (isEdit) payload.status = form.status;

    try {
      if (isEdit) {
        await axios.patch(`${API}/animals/${initial.id}`, payload, {
          withCredentials: true,
        });
        toast.success(t.app.animals.saved_toast);
      } else {
        await axios.post(`${API}/animals`, payload, {
          withCredentials: true,
        });
        toast.success(t.app.animals.created_toast);
      }
      onSaved?.();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
      </DialogHeader>
      <form
        onSubmit={submit}
        data-testid="animal-form"
        className="grid grid-cols-2 gap-4 mt-2"
      >
        {/* Photo section */}
        <div className="col-span-2">
          <Label className="text-[#5C6B5C] text-xs uppercase tracking-wider font-semibold">Photo de l'animal</Label>
          <div className="mt-2">
            <PhotoCapture value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} />
          </div>
        </div>
        <div>
          <Label>{t.app.animals.tag_label}</Label>
          <Input
            data-testid="animal-tag"
            required
            value={form.tag}
            onChange={(e) => setForm({ ...form, tag: e.target.value })}
            placeholder="TN-001"
            className="mt-1.5 h-11 bg-white font-mono"
          />
        </div>
        <div>
          <Label>{t.app.animals.name_label}</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Bella"
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div>
          <Label>{t.app.animals.species_label}</Label>
          <Select
            value={form.species}
            onValueChange={(v) => setForm({ ...form, species: v })}
          >
            <SelectTrigger className="mt-1.5 h-11 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPECIES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t.app.animals.species[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t.app.animals.breed_label}</Label>
          <Input
            value={form.breed}
            onChange={(e) => setForm({ ...form, breed: e.target.value })}
            placeholder="Holstein"
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div>
          <Label>{t.app.animals.sex_label}</Label>
          <Select
            value={form.sex}
            onValueChange={(v) => setForm({ ...form, sex: v })}
          >
            <SelectTrigger className="mt-1.5 h-11 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="F">{t.app.animals.female}</SelectItem>
              <SelectItem value="M">{t.app.animals.male}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t.app.animals.birth_label}</Label>
          <Input
            type="date"
            value={form.birth_date}
            onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        <div>
          <Label>{t.app.animals.weight_label}</Label>
          <Input
            type="number"
            min={0}
            step={0.1}
            value={form.weight_kg}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
            className="mt-1.5 h-11 bg-white"
          />
        </div>
        {isEdit && (
          <div>
            <Label>{t.app.animals.status_label}</Label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
            >
              <SelectTrigger className="mt-1.5 h-11 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t.app.status.active}</SelectItem>
                <SelectItem value="sold">{t.app.status.sold}</SelectItem>
                <SelectItem value="dead">{t.app.status.dead}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="col-span-2">
          <Label>{t.app.animals.notes_label}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="mt-1.5 bg-white resize-none"
            placeholder={t.app.animals.notes_placeholder}
          />
        </div>

        {/* Filiation */}
        <div className="col-span-2 mt-2 pt-4 border-t border-[#E2E4DF]">
          <Label className="text-[#5C6B5C] text-xs uppercase tracking-wider font-semibold">Filiation (optionnelle)</Label>
        </div>
        <div>
          <Label>Mère (Maman)</Label>
          <Select
            value={form.mother_id || "none"}
            onValueChange={(v) => setForm({ ...form, mother_id: v === "none" ? "" : v })}
          >
            <SelectTrigger data-testid="animal-mother" className="mt-1.5 h-11 bg-white">
              <SelectValue placeholder="— Aucune —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Aucune —</SelectItem>
              {mothers.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.tag}{m.name ? ` · ${m.name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Père (Papa)</Label>
          <Select
            value={form.father_id || "none"}
            onValueChange={(v) => setForm({ ...form, father_id: v === "none" ? "" : v })}
          >
            <SelectTrigger data-testid="animal-father" className="mt-1.5 h-11 bg-white">
              <SelectValue placeholder="— Aucun —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Aucun —</SelectItem>
              {fathers.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.tag}{f.name ? ` · ${f.name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Origine */}
        <div className="col-span-2 mt-2 pt-4 border-t border-[#E2E4DF]">
          <Label className="text-[#5C6B5C] text-xs uppercase tracking-wider font-semibold">Origine de l'animal</Label>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, origin: "born_farm" })}
              data-testid="origin-born-farm"
              className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-colors ${form.origin === "born_farm" ? "border-[#1C7A3C] bg-[#1C7A3C]/10 text-[#1C7A3C]" : "border-[#E2E4DF] bg-white text-[#5C6B5C] hover:border-[#1C3A27]"}`}
            >
              🏡 Né(e) à la ferme
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, origin: "purchased" })}
              data-testid="origin-purchased"
              className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-colors ${form.origin === "purchased" ? "border-[#E05A3D] bg-[#E05A3D]/10 text-[#C84B31]" : "border-[#E2E4DF] bg-white text-[#5C6B5C] hover:border-[#1C3A27]"}`}
            >
              💰 Acheté(e)
            </button>
          </div>
        </div>
        {form.origin === "purchased" && (
          <>
            <div>
              <Label>Prix d'achat (DT)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.purchase_price_tnd}
                onChange={(e) => setForm({ ...form, purchase_price_tnd: e.target.value })}
                placeholder="Ex: 1200"
                data-testid="animal-price"
                className="mt-1.5 h-11 bg-white"
              />
            </div>
            <div>
              <Label>Date d'achat</Label>
              <Input
                type="date"
                value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                data-testid="animal-purchase-date"
                className="mt-1.5 h-11 bg-white"
              />
            </div>
            <div className="col-span-2">
              <Label>Fournisseur / Vendeur (optionnel)</Label>
              <Input
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                placeholder="Ex: Souk El Khemis, marché de Sfax…"
                data-testid="animal-supplier"
                className="mt-1.5 h-11 bg-white"
              />
            </div>
          </>
        )}

        {error && (
          <div className="col-span-2 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">
            {error}
          </div>
        )}
        <DialogFooter className="col-span-2 mt-1">
          <Button type="button" variant="outline" onClick={onClose}>
            {t.app.common.cancel}
          </Button>
          <Button
            type="submit"
            data-testid="submit-animal"
            disabled={submitting}
            className="bg-[#1C3A27] hover:bg-[#142A1C] text-white"
          >
            {submitting ? t.app.common.saving : isEdit ? t.app.common.update : t.app.common.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
